import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GetAppIcon from '@mui/icons-material/GetApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CodeIcon from '@mui/icons-material/Code';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import NotesIcon from '@mui/icons-material/Notes';
import data from '../data/ragGroundTruthStratified.json';

const STORAGE_KEY = 'nscat_stratified_gt_overrides';

const VERDICT_COLOR = { c: 'success', p: 'warning', w: 'error' };
const VERDICT_LABEL = { c: 'CORRECT', p: 'PARTIAL', w: 'WRONG' };
const VERDICT_ICON = {
  c: <CheckCircleIcon fontSize="small" />,
  p: <RemoveCircleIcon fontSize="small" />,
  w: <CancelIcon fontSize="small" />,
};

const VENDOR_LABEL = {
  cisco_ftd: 'Cisco FTD',
  paloalto_panos: 'Palo Alto',
  juniper_srx: 'Juniper SRX',
  fortinet_fortigate: 'Fortinet',
  huawei_usg: 'Huawei USG',
  checkpoint_security_gateway: 'Checkpoint',
};

// Normalise a control id like "SC-7", "SC-07", "SC-7(11)" -> "SC-07(11)"
const normaliseId = (fam, num, enh) => `${fam}-${String(parseInt(num, 10)).padStart(2, '0')}${enh || ''}`;
const CTRL_RE = /\b([A-Z]{2})-(\d+)(\(\d+\))?\b/g;

function ControlBadge({ raw, fam, num, enh, lookup }) {
  const id = normaliseId(fam, num, enh);
  const ctrl = lookup?.[id];
  const tooltipBody = ctrl ? (
    <Box sx={{ maxWidth: 460 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'inherit' }}>
        §{id} — {ctrl.title}
      </Typography>
      {ctrl.family_title && (
        <Typography variant="caption" sx={{ display: 'block', color: 'inherit', opacity: 0.8 }}>
          {ctrl.family_title}
        </Typography>
      )}
      {ctrl.description && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'inherit' }}>
          {ctrl.description}
        </Typography>
      )}
    </Box>
  ) : `§${id} — (not in catalog)`;

  return (
    <Tooltip title={tooltipBody} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          fontFamily: 'monospace',
          fontWeight: 600,
          color: 'primary.main',
          bgcolor: 'rgba(33,150,243,0.12)',
          px: 0.6,
          py: 0.1,
          borderRadius: 0.5,
          cursor: 'help',
          textDecoration: 'underline dotted',
          textDecorationColor: 'rgba(33,150,243,0.6)',
        }}
      >
        {raw}
      </Box>
    </Tooltip>
  );
}

function RichText({ text, lookup }) {
  if (!text) return null;
  const parts = [];
  let lastIdx = 0;
  let m;
  // Reset regex state
  const re = new RegExp(CTRL_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(<React.Fragment key={`t-${lastIdx}`}>{text.slice(lastIdx, m.index)}</React.Fragment>);
    }
    parts.push(
      <ControlBadge
        key={`c-${m.index}`}
        raw={m[0]}
        fam={m[1]}
        num={m[2]}
        enh={m[3]}
        lookup={lookup}
      />
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    parts.push(<React.Fragment key={`t-${lastIdx}`}>{text.slice(lastIdx)}</React.Fragment>);
  }
  return <>{parts}</>;
}

function StatCard({ label, value, sub, color = 'primary.main' }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color, mt: 0.5 }}>
          {value}
        </Typography>
        {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function loadOverrides() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function saveOverrides(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (e) {
    console.error('Failed to save overrides', e);
  }
}

function computeStats(findings) {
  const fam = {};
  const ven = {};
  let c = 0, p = 0, w = 0;
  for (const f of findings) {
    if (f.verdict === 'c') c++;
    else if (f.verdict === 'p') p++;
    else if (f.verdict === 'w') w++;
    if (!fam[f.family]) fam[f.family] = { family: f.family, n: 0, c: 0, p: 0, w: 0 };
    fam[f.family].n++;
    fam[f.family][f.verdict] = (fam[f.family][f.verdict] || 0) + 1;
    if (!ven[f.vendor]) ven[f.vendor] = { vendor: f.vendor, n: 0, c: 0, p: 0, w: 0 };
    ven[f.vendor].n++;
    ven[f.vendor][f.verdict] = (ven[f.vendor][f.verdict] || 0) + 1;
  }
  const finish = (row) => ({
    ...row,
    strict_pct: row.n ? Math.round((1000 * row.c) / row.n) / 10 : 0,
    lenient_pct: row.n ? Math.round((1000 * (row.c + 0.5 * row.p)) / row.n) / 10 : 0,
  });
  const family_stats = Object.values(fam).sort((a, b) => b.n - a.n).map(finish);
  const vendor_stats = Object.values(ven).sort((a, b) => b.n - a.n).map(finish);
  const n = findings.length;
  const headline = {
    n,
    c, p, w,
    strict_pct: n ? Math.round((1000 * c) / n) / 10 : 0,
    lenient_pct: n ? Math.round((1000 * (c + 0.5 * p)) / n) / 10 : 0,
  };
  return { headline, family_stats, vendor_stats };
}

// ── Metric formatters (paper-ready exports) ──

function buildMarkdownReport(liveStats, preStats, editedCount, sampleBasis) {
  const h = liveStats.headline;
  const deltaStr = (live, base) => {
    const d = live - base;
    return `${d >= 0 ? '+' : ''}${d.toFixed(1)}pp`;
  };
  const editedNote = editedCount > 0
    ? `\n> **Note:** ${editedCount} finding(s) overridden from baseline. Strict delta vs baseline: ${deltaStr(h.strict_pct, preStats.headline.strict_pct)}, lenient delta: ${deltaStr(h.lenient_pct, preStats.headline.lenient_pct)}.\n`
    : '';

  const famRows = liveStats.family_stats
    .map((r) => `| ${r.family} | ${r.n} | ${r.c} | ${r.p} | ${r.w} | ${r.strict_pct}% | ${r.lenient_pct}% |`)
    .join('\n');
  const venRows = liveStats.vendor_stats
    .map((r) => `| ${r.vendor} | ${r.n} | ${r.c} | ${r.p} | ${r.w} | ${r.strict_pct}% | ${r.lenient_pct}% |`)
    .join('\n');

  return `# RAG NIST Mapping — Stratified Ground Truth (Paper 2)

**Sample basis:** ${sampleBasis}
**Scoring:** Top-1 accuracy with strict (c only) and lenient (c + 0.5·p) schemes.
**Rubric:** c=correct, p=partial (right family or adjacent area, sub-control off), w=wrong (different security domain).
${editedNote}
## Headline

| Metric | Value |
|---|---:|
| Sample size (N) | ${h.n} |
| Correct (c) | ${h.c} |
| Partial (p) | ${h.p} |
| Wrong (w) | ${h.w} |
| **Top-1 strict accuracy** | **${h.strict_pct}%** |
| **Top-1 lenient accuracy** | **${h.lenient_pct}%** |

## Per-NIST-Family Breakdown

| Family | N | C | P | W | Strict | Lenient |
|---|---:|---:|---:|---:|---:|---:|
${famRows}

## Per-Vendor Breakdown

| Vendor | N | C | P | W | Strict | Lenient |
|---|---:|---:|---:|---:|---:|---:|
${venRows}

## Paper-Ready One-Liner

> RAG top-1 accuracy on the latest-100 cohort was ${h.strict_pct}% strict and ${h.lenient_pct}% lenient (N=${h.n}; c=${h.c}, p=${h.p}, w=${h.w}), demonstrating that the 99.96% hit rate measures retrieval coverage rather than mapping correctness.

*Dataset and full per-finding annotations: https://nscat.securemango.com/mvp-overview/rag-ground-truth*
`;
}

function buildLatexReport(liveStats, preStats, editedCount, sampleBasis) {
  const h = liveStats.headline;
  const famRows = liveStats.family_stats
    .map((r) => `${r.family} & ${r.n} & ${r.c} & ${r.p} & ${r.w} & ${r.strict_pct}\\% & ${r.lenient_pct}\\% \\\\`)
    .join('\n');
  const venRows = liveStats.vendor_stats
    .map((r) => {
      const v = r.vendor.replace(/_/g, ' ');
      return `${v} & ${r.n} & ${r.c} & ${r.p} & ${r.w} & ${r.strict_pct}\\% & ${r.lenient_pct}\\% \\\\`;
    })
    .join('\n');
  const editedNote = editedCount > 0
    ? `% NOTE: ${editedCount} finding(s) overridden from baseline.\n`
    : '';

  return `% ============================================================
% RAG NIST Mapping — Stratified Sample Precision Metrics (Paper 2 LaTeX)
% Sample: ${sampleBasis}
% Scoring: strict (c only); lenient (c + 0.5*p)
${editedNote}% ============================================================

% --- Headline numbers (one-liner for prose / abstract) ---
% Strict top-1 accuracy: ${h.strict_pct}\\% (${h.c}/${h.n})
% Lenient top-1 accuracy: ${h.lenient_pct}\\% (c+0.5p)
% Distribution: ${h.c}c / ${h.p}p / ${h.w}w

% --- Table: precision by NIST family ---
\\begin{table}[!t]
\\caption{RAG NIST Mapping Precision by Family (Stratified Sample, Paper 2)}
\\label{tab:rag_precision}
\\centering
\\scriptsize
\\begin{tabular}{@{}lrrrrrr@{}}
\\toprule
\\textbf{Family} & \\textbf{N} & \\textbf{C} & \\textbf{P} & \\textbf{W} & \\textbf{Strict} & \\textbf{Lenient} \\\\
\\midrule
${famRows}
\\midrule
\\textbf{All} & \\textbf{${h.n}} & \\textbf{${h.c}} & \\textbf{${h.p}} & \\textbf{${h.w}} & \\textbf{${h.strict_pct}\\%} & \\textbf{${h.lenient_pct}\\%} \\\\
\\bottomrule
\\end{tabular}
\\vspace{1mm}
\\raggedright\\tiny Note: ${sampleBasis} Strict = c only. Lenient = c + 0.5p. Full per-finding annotations available at \\url{https://nscat.securemango.com/mvp-overview/stratified-ground-truth}.
\\end{table}

% --- Table: precision by vendor ---
\\begin{table}[!t]
\\caption{RAG NIST Mapping Precision by Vendor (Stratified Sample, Paper 2)}
\\label{tab:rag_precision_vendor}
\\centering
\\scriptsize
\\begin{tabular}{@{}lrrrrrr@{}}
\\toprule
\\textbf{Vendor} & \\textbf{N} & \\textbf{C} & \\textbf{P} & \\textbf{W} & \\textbf{Strict} & \\textbf{Lenient} \\\\
\\midrule
${venRows}
\\midrule
\\textbf{All} & \\textbf{${h.n}} & \\textbf{${h.c}} & \\textbf{${h.p}} & \\textbf{${h.w}} & \\textbf{${h.strict_pct}\\%} & \\textbf{${h.lenient_pct}\\%} \\\\
\\bottomrule
\\end{tabular}
\\end{table}

% --- Suggested Design Validation row (insert into Table V / DQ summary) ---
% DQ3 & Top-1 mapping accuracy (latest-100 cohort) & ${h.strict_pct}\\% strict / ${h.lenient_pct}\\% lenient & Annotation \\\\
`;
}

function buildPlainTextReport(liveStats, preStats, editedCount, sampleBasis) {
  const h = liveStats.headline;
  const pad = (s, n) => String(s).padEnd(n);
  const padR = (s, n) => String(s).padStart(n);

  const famLines = liveStats.family_stats
    .map((r) => `  ${pad(r.family, 8)} ${padR(r.n, 4)} ${padR(r.c, 4)} ${padR(r.p, 4)} ${padR(r.w, 4)} ${padR(r.strict_pct + '%', 8)} ${padR(r.lenient_pct + '%', 9)}`)
    .join('\n');
  const venLines = liveStats.vendor_stats
    .map((r) => `  ${pad(r.vendor, 28)} ${padR(r.n, 4)} ${padR(r.c, 4)} ${padR(r.p, 4)} ${padR(r.w, 4)} ${padR(r.strict_pct + '%', 8)} ${padR(r.lenient_pct + '%', 9)}`)
    .join('\n');
  const editedNote = editedCount > 0
    ? `\nNote: ${editedCount} finding(s) overridden from baseline. Strict ${(h.strict_pct - preStats.headline.strict_pct).toFixed(1)}pp / Lenient ${(h.lenient_pct - preStats.headline.lenient_pct).toFixed(1)}pp vs baseline.\n`
    : '';

  return `RAG NIST Mapping — Stratified Ground Truth (Paper 2)
===================================================

Sample basis: ${sampleBasis}
Scoring:      Top-1 strict (c only) and lenient (c + 0.5*p)
Rubric:       c=correct, p=partial, w=wrong
${editedNote}
Headline
--------
  Sample size (N): ${h.n}
  Correct  (c):    ${h.c}
  Partial  (p):    ${h.p}
  Wrong    (w):    ${h.w}
  Top-1 strict:    ${h.strict_pct}%
  Top-1 lenient:   ${h.lenient_pct}%

Per-NIST-Family Breakdown
-------------------------
  ${pad('Family', 8)} ${padR('N', 4)} ${padR('C', 4)} ${padR('P', 4)} ${padR('W', 4)} ${padR('Strict', 8)} ${padR('Lenient', 9)}
${famLines}

Per-Vendor Breakdown
--------------------
  ${pad('Vendor', 28)} ${padR('N', 4)} ${padR('C', 4)} ${padR('P', 4)} ${padR('W', 4)} ${padR('Strict', 8)} ${padR('Lenient', 9)}
${venLines}

Dataset: https://nscat.securemango.com/mvp-overview/rag-ground-truth
`;
}

export default function StratifiedGroundTruth() {
  const navigate = useNavigate();
  const [overrides, setOverridesState] = useState(() => loadOverrides());
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [editedFilter, setEditedFilter] = useState('all'); // all | edited | original
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState(null);
  const [editDialog, setEditDialog] = useState(null); // {finding, draft}
  const [resetConfirm, setResetConfirm] = useState(false);
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);

  // Merge pre-annotations with user overrides
  const mergedFindings = useMemo(() => {
    return data.findings.map((f) => {
      const ov = overrides[f.gt_id];
      if (!ov) return { ...f, _edited: false };
      return {
        ...f,
        verdict: ov.verdict ?? f.verdict,
        suggested: ov.suggested ?? f.suggested,
        rationale: ov.rationale ?? f.rationale,
        _userNotes: ov.notes || '',
        _edited: true,
        _originalVerdict: f.verdict,
        _originalSuggested: f.suggested,
        _originalRationale: f.rationale,
      };
    });
  }, [overrides]);

  const liveStats = useMemo(() => computeStats(mergedFindings), [mergedFindings]);
  const editedCount = useMemo(() => Object.keys(overrides).length, [overrides]);

  const filtered = useMemo(() => {
    return mergedFindings.filter((f) => {
      if (verdictFilter !== 'all' && f.verdict !== verdictFilter) return false;
      if (familyFilter !== 'all' && f.family !== familyFilter) return false;
      if (vendorFilter !== 'all' && f.vendor !== vendorFilter) return false;
      if (editedFilter === 'edited' && !f._edited) return false;
      if (editedFilter === 'original' && f._edited) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [f.rule, f.issue, f.recommendation, f.assigned_nist, f.suggested].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [mergedFindings, verdictFilter, familyFilter, vendorFilter, editedFilter, search]);

  const familyOptions = useMemo(() => {
    const set = new Set(data.findings.map((f) => f.family));
    return ['all', ...Array.from(set).sort()];
  }, []);

  const vendorOptions = useMemo(() => {
    const set = new Set(data.findings.map((f) => f.vendor));
    return ['all', ...Array.from(set).sort()];
  }, []);

  const persistOverrides = useCallback((next) => {
    setOverridesState(next);
    saveOverrides(next);
  }, []);

  const openEdit = (finding) => {
    setEditDialog({
      finding,
      draft: {
        verdict: finding.verdict,
        suggested: finding.suggested || '',
        rationale: finding.rationale || '',
        notes: finding._userNotes || '',
      },
    });
  };

  const closeEdit = () => setEditDialog(null);

  const saveEdit = () => {
    if (!editDialog) return;
    const { finding, draft } = editDialog;
    const orig = data.findings.find((f) => f.gt_id === finding.gt_id);
    const isUnchanged =
      draft.verdict === orig.verdict &&
      (draft.suggested || '') === (orig.suggested || '') &&
      (draft.rationale || '') === (orig.rationale || '') &&
      !draft.notes;
    const next = { ...overrides };
    if (isUnchanged) {
      delete next[finding.gt_id];
    } else {
      next[finding.gt_id] = {
        verdict: draft.verdict,
        suggested: draft.suggested,
        rationale: draft.rationale,
        notes: draft.notes,
        edited_at: new Date().toISOString(),
      };
    }
    persistOverrides(next);
    setSnackbar(isUnchanged ? `Reverted finding #${finding.gt_id}` : `Saved finding #${finding.gt_id}`);
    closeEdit();
  };

  const resetRow = (gt_id) => {
    const next = { ...overrides };
    delete next[gt_id];
    persistOverrides(next);
    setSnackbar(`Reset finding #${gt_id} to baseline`);
  };

  const resetAll = () => {
    persistOverrides({});
    setResetConfirm(false);
    setSnackbar('All overrides cleared');
  };

  const handleDownload = () => {
    const bundle = {
      ...data,
      headline: liveStats.headline,
      family_stats: liveStats.family_stats,
      vendor_stats: liveStats.vendor_stats,
      findings: mergedFindings.map((f) => {
        const ov = overrides[f.gt_id];
        if (!ov) return data.findings.find((x) => x.gt_id === f.gt_id);
        const orig = data.findings.find((x) => x.gt_id === f.gt_id);
        return {
          ...orig,
          verdict: ov.verdict,
          suggested: ov.suggested,
          rationale: ov.rationale,
          notes: ov.notes,
          edited_at: ov.edited_at,
          pre_annotation: {
            verdict: orig.verdict,
            suggested: orig.suggested,
            rationale: orig.rationale,
          },
        };
      }),
      export_metadata: {
        exported_at: new Date().toISOString(),
        total_overrides: editedCount,
        live_stats: liveStats.headline,
      },
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rag_ground_truth_latest100_${editedCount > 0 ? 'edited' : 'original'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyMetrics = (format) => {
    let text = '';
    const sampleBasis = data.methodology.sample_basis;
    if (format === 'markdown') {
      text = buildMarkdownReport(liveStats, preStats, editedCount, sampleBasis);
    } else if (format === 'latex') {
      text = buildLatexReport(liveStats, preStats, editedCount, sampleBasis);
    } else if (format === 'plain') {
      text = buildPlainTextReport(liveStats, preStats, editedCount, sampleBasis);
    } else if (format === 'headline') {
      const h = liveStats.headline;
      text = `RAG NIST mapping top-1 accuracy: ${h.strict_pct}% strict / ${h.lenient_pct}% lenient (N=${h.n}; c=${h.c}, p=${h.p}, w=${h.w}; ${sampleBasis})`;
    }
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar(`Copied ${format} metrics to clipboard (${text.length} chars)`);
    }).catch(() => {
      setSnackbar('Copy failed — clipboard access denied');
    });
    setCopyMenuAnchor(null);
  };

  const handleCopyRow = (finding) => {
    const text = `Finding #${finding.gt_id} (${finding.vendor}, ${finding.severity})\nRule: ${finding.rule}\nIssue: ${finding.issue}\nRecommendation: ${finding.recommendation}\nRAG assigned: ${finding.assigned_nist} (similarity ${finding.similarity_score})\nVerdict: ${VERDICT_LABEL[finding.verdict]}${finding.suggested ? ` → suggested: ${finding.suggested}` : ''}\nRationale: ${finding.rationale}`;
    navigator.clipboard.writeText(text);
    setSnackbar(`Copied finding #${finding.gt_id}`);
  };

  // Compute pre-annotation totals (constant from data) for delta display
  const preStats = useMemo(() => computeStats(data.findings.map((f) => ({ ...f, _edited: false }))), []);
  const liveDelta = {
    strict: liveStats.headline.strict_pct - preStats.headline.strict_pct,
    lenient: liveStats.headline.lenient_pct - preStats.headline.lenient_pct,
  };
  const fmtDelta = (d) => `${d >= 0 ? '+' : ''}${d.toFixed(1)}`;

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/mvp-overview')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            RAG NIST Mapping — Stratified Ground Truth (Paper 2)
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {editedCount > 0 && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setResetConfirm(true)}
            >
              Reset All Overrides ({editedCount})
            </Button>
          )}
          <Tooltip title="Copy paper-ready metrics in your chosen format">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
            >
              Copy Metrics
            </Button>
          </Tooltip>
          <Menu
            anchorEl={copyMenuAnchor}
            open={!!copyMenuAnchor}
            onClose={() => setCopyMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { minWidth: 280 } }}
          >
            <MenuItem onClick={() => handleCopyMetrics('markdown')}>
              <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Markdown (paper-ready)"
                secondary="Headline + per-family + per-vendor tables"
              />
            </MenuItem>
            <MenuItem onClick={() => handleCopyMetrics('latex')}>
              <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="LaTeX (drop into paper)"
                secondary="IEEEtran-style tables + comments"
              />
            </MenuItem>
            <MenuItem onClick={() => handleCopyMetrics('plain')}>
              <ListItemIcon><NotesIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Plain text (chat / email)"
                secondary="Monospace-aligned summary"
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleCopyMetrics('headline')}>
              <ListItemIcon><AssessmentIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Headline only"
                secondary="N, c/p/w, strict%, lenient% on one line"
              />
            </MenuItem>
          </Menu>
          <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleDownload}>
            Download {editedCount > 0 ? 'Edited' : ''} JSON
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Per-finding precision evaluation of the RAG-based NIST SP 800-53 mapping
        pipeline. Sample: <strong>{data.methodology.sample_basis}</strong>.
        Annotator: <em>{data.methodology.annotator}</em>. {data.methodology.scoring}
      </Typography>

      {editedCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have <strong>{editedCount} override{editedCount === 1 ? '' : 's'}</strong> saved
          (persisted in this browser's localStorage). Stats below reflect your edits.
          Baseline was {preStats.headline.strict_pct}% strict / {preStats.headline.lenient_pct}% lenient;
          current is {liveStats.headline.strict_pct}% strict ({fmtDelta(liveDelta.strict)}pp)
          and {liveStats.headline.lenient_pct}% lenient ({fmtDelta(liveDelta.lenient)}pp).
        </Alert>
      )}

      {/* Headline stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Sample size" value={liveStats.headline.n} sub="latest 100 findings" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Top-1 strict"
            value={`${liveStats.headline.strict_pct}%`}
            sub={`${liveStats.headline.c} correct / ${liveStats.headline.n}`}
            color="success.main"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Top-1 lenient"
            value={`${liveStats.headline.lenient_pct}%`}
            sub="c + 0.5·p"
            color="warning.main"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Wrong assignments"
            value={liveStats.headline.w}
            sub={`${Math.round((100 * liveStats.headline.w) / liveStats.headline.n)}% of sample`}
            color="error.main"
          />
        </Grid>
      </Grid>

      {/* Rubric */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Annotation rubric</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
          {Object.entries(data.methodology.rubric).map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip
                icon={VERDICT_ICON[k]}
                label={VERDICT_LABEL[k]}
                color={VERDICT_COLOR[k]}
                size="small"
                sx={{ flexShrink: 0 }}
              />
              <Typography variant="body2" color="text.secondary">{v}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Per-family + Per-vendor breakdowns */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Per-NIST-Family Breakdown
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Family</strong></TableCell>
                    <TableCell align="right"><strong>N</strong></TableCell>
                    <TableCell align="right">C</TableCell>
                    <TableCell align="right">P</TableCell>
                    <TableCell align="right">W</TableCell>
                    <TableCell align="right"><strong>Strict</strong></TableCell>
                    <TableCell align="right"><strong>Lenient</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveStats.family_stats.map((row) => (
                    <TableRow key={row.family} hover>
                      <TableCell><strong>{row.family}</strong></TableCell>
                      <TableCell align="right">{row.n}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{row.c}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>{row.p}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{row.w}</TableCell>
                      <TableCell align="right">{row.strict_pct}%</TableCell>
                      <TableCell align="right">{row.lenient_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Per-Vendor Breakdown
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Vendor</strong></TableCell>
                    <TableCell align="right"><strong>N</strong></TableCell>
                    <TableCell align="right">C</TableCell>
                    <TableCell align="right">P</TableCell>
                    <TableCell align="right">W</TableCell>
                    <TableCell align="right"><strong>Strict</strong></TableCell>
                    <TableCell align="right"><strong>Lenient</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveStats.vendor_stats.map((row) => (
                    <TableRow key={row.vendor} hover>
                      <TableCell>{VENDOR_LABEL[row.vendor] || row.vendor}</TableCell>
                      <TableCell align="right">{row.n}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{row.c}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>{row.p}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{row.w}</TableCell>
                      <TableCell align="right">{row.strict_pct}%</TableCell>
                      <TableCell align="right">{row.lenient_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            value={verdictFilter}
            exclusive
            onChange={(_, v) => v && setVerdictFilter(v)}
          >
            <ToggleButton value="all">All ({liveStats.headline.n})</ToggleButton>
            <ToggleButton value="c" sx={{ color: 'success.main' }}>Correct ({liveStats.headline.c})</ToggleButton>
            <ToggleButton value="p" sx={{ color: 'warning.main' }}>Partial ({liveStats.headline.p})</ToggleButton>
            <ToggleButton value="w" sx={{ color: 'error.main' }}>Wrong ({liveStats.headline.w})</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            value={editedFilter}
            exclusive
            onChange={(_, v) => v && setEditedFilter(v)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="edited">Edited ({editedCount})</ToggleButton>
            <ToggleButton value="original">Original</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            select
            label="Family"
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 120 }}
          >
            {familyOptions.map((f) => <option key={f} value={f}>{f === 'all' ? 'All families' : f}</option>)}
          </TextField>
          <TextField
            size="small"
            select
            label="Vendor"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 180 }}
          >
            {vendorOptions.map((v) => (
              <option key={v} value={v}>{v === 'all' ? 'All vendors' : (VENDOR_LABEL[v] || v)}</option>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search rule, issue, control..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: 250 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Showing {filtered.length} of {liveStats.headline.n}
          </Typography>
        </Stack>
      </Paper>

      {/* Findings table */}
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50 }}>#</TableCell>
                <TableCell sx={{ width: 100 }}>Verdict</TableCell>
                <TableCell sx={{ width: 240 }}>RAG Assigned</TableCell>
                <TableCell sx={{ width: 220 }}>Suggested Correct</TableCell>
                <TableCell sx={{ width: 80 }}>Family</TableCell>
                <TableCell sx={{ width: 110 }}>Vendor</TableCell>
                <TableCell sx={{ width: 80 }}>Severity</TableCell>
                <TableCell>Rule / Issue / Recommendation</TableCell>
                <TableCell>Rationale</TableCell>
                <TableCell sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((f) => (
                <TableRow
                  key={f.gt_id}
                  hover
                  sx={{
                    verticalAlign: 'top',
                    backgroundColor: f._edited ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                  }}
                >
                  <TableCell>
                    {f._edited ? (
                      <Tooltip title={`Edited from "${VERDICT_LABEL[f._originalVerdict]}"`}>
                        <Badge color="info" variant="dot">
                          {f.gt_id}
                        </Badge>
                      </Tooltip>
                    ) : f.gt_id}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={VERDICT_ICON[f.verdict]}
                      label={VERDICT_LABEL[f.verdict]}
                      color={VERDICT_COLOR[f.verdict]}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      §{f.assigned_nist}
                    </Typography>
                    {f.assigned_nist_details?.title && (
                      <Tooltip title={f.assigned_nist_details.description || ''} placement="right" arrow>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', fontWeight: 500, color: 'text.primary', cursor: 'help' }}
                        >
                          {f.assigned_nist_details.title}
                        </Typography>
                      </Tooltip>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      sim {f.similarity_score?.toFixed?.(2) ?? f.similarity_score}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {f.suggested ? (
                      <>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          §{f.suggested}
                        </Typography>
                        {f.suggested_details?.title && (
                          <Tooltip title={f.suggested_details.description || ''} placement="right" arrow>
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', fontWeight: 500, color: 'text.primary', cursor: 'help' }}
                            >
                              {f.suggested_details.title}
                            </Typography>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{f.family}</TableCell>
                  <TableCell>{VENDOR_LABEL[f.vendor] || f.vendor}</TableCell>
                  <TableCell>{f.severity}</TableCell>
                  <TableCell sx={{ maxWidth: 460 }}>
                    {f.rule && (
                      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'primary.main', mb: 0.5 }}>
                        {f.rule}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5 }} component="div">
                      <RichText text={f.issue} lookup={data.controls_lookup} />
                    </Typography>
                    {f.recommendation && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} component="div">
                        → <RichText text={f.recommendation} lookup={data.controls_lookup} />
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" color="text.secondary" component="div">
                      <RichText text={f.rationale} lookup={data.controls_lookup} />
                    </Typography>
                    {f._userNotes && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'info.main' }} component="div">
                        Notes: <RichText text={f._userNotes} lookup={data.controls_lookup} />
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit verdict">
                        <IconButton size="small" onClick={() => openEdit(f)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {f._edited && (
                        <Tooltip title="Reset to baseline">
                          <IconButton size="small" onClick={() => resetRow(f.gt_id)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Copy this row">
                        <IconButton size="small" onClick={() => handleCopyRow(f)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No findings match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit dialog */}
      <Dialog open={!!editDialog} onClose={closeEdit} maxWidth="md" fullWidth>
        {editDialog && (
          <>
            <DialogTitle>
              Edit Finding #{editDialog.finding.gt_id}
              {editDialog.finding._edited && (
                <Chip label="EDITED" color="info" size="small" sx={{ ml: 1 }} />
              )}
            </DialogTitle>
            <DialogContent dividers>
              {/* Finding context (read-only) */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip size="small" label={editDialog.finding.severity} />
                  <Chip size="small" label={VENDOR_LABEL[editDialog.finding.vendor] || editDialog.finding.vendor} />
                  <Chip size="small" label={`Family: ${editDialog.finding.family}`} />
                </Stack>
                {editDialog.finding.rule && (
                  <>
                    <Typography variant="caption" color="text.secondary">RULE</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', mb: 1 }}>
                      {editDialog.finding.rule}
                    </Typography>
                  </>
                )}
                <Typography variant="caption" color="text.secondary">ISSUE</Typography>
                <Typography variant="body2" sx={{ mb: 1 }} component="div">
                  <RichText text={editDialog.finding.issue} lookup={data.controls_lookup} />
                </Typography>
                {editDialog.finding.recommendation && (
                  <>
                    <Typography variant="caption" color="text.secondary">RECOMMENDATION</Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }} component="div">
                      → <RichText text={editDialog.finding.recommendation} lookup={data.controls_lookup} />
                    </Typography>
                  </>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">RAG ASSIGNED NIST CONTROL</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  §{editDialog.finding.assigned_nist}
                  {editDialog.finding.assigned_nist_details?.title && (
                    <> — {editDialog.finding.assigned_nist_details.title}</>
                  )}
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontFamily: 'inherit', fontWeight: 400 }}>
                    (similarity {editDialog.finding.similarity_score})
                  </Typography>
                </Typography>
                {editDialog.finding.assigned_nist_details?.family_title && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {editDialog.finding.assigned_nist_details.family_title}
                  </Typography>
                )}
                {editDialog.finding.assigned_nist_details?.description && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                    {editDialog.finding.assigned_nist_details.description}
                  </Typography>
                )}
                {editDialog.finding.suggested_details?.title && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">SUGGESTED CORRECT CONTROL</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      §{editDialog.finding.suggested} — {editDialog.finding.suggested_details.title}
                    </Typography>
                    {editDialog.finding.suggested_details.family_title && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {editDialog.finding.suggested_details.family_title}
                      </Typography>
                    )}
                    {editDialog.finding.suggested_details.description && (
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                        {editDialog.finding.suggested_details.description}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>

              {editDialog.finding._edited && (
                <Alert severity="info" icon={<HistoryEduIcon />} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Baseline:</strong> {VERDICT_LABEL[editDialog.finding._originalVerdict]}
                    {editDialog.finding._originalSuggested && ` → suggested ${editDialog.finding._originalSuggested}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    <RichText text={editDialog.finding._originalRationale} lookup={data.controls_lookup} />
                  </Typography>
                </Alert>
              )}

              {/* Editable fields */}
              <Typography variant="subtitle2" gutterBottom>Verdict</Typography>
              <ToggleButtonGroup
                exclusive
                value={editDialog.draft.verdict}
                onChange={(_, v) => v && setEditDialog({ ...editDialog, draft: { ...editDialog.draft, verdict: v } })}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="c" sx={{ color: 'success.main', '&.Mui-selected': { bgcolor: 'success.main', color: 'white' } }}>
                  <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> Correct
                </ToggleButton>
                <ToggleButton value="p" sx={{ color: 'warning.main', '&.Mui-selected': { bgcolor: 'warning.main', color: 'white' } }}>
                  <RemoveCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> Partial
                </ToggleButton>
                <ToggleButton value="w" sx={{ color: 'error.main', '&.Mui-selected': { bgcolor: 'error.main', color: 'white' } }}>
                  <CancelIcon fontSize="small" sx={{ mr: 0.5 }} /> Wrong
                </ToggleButton>
              </ToggleButtonGroup>

              <TextField
                fullWidth
                label="Suggested correct control (e.g. SC-13, AC-7, IA-05(01))"
                value={editDialog.draft.suggested}
                onChange={(e) => setEditDialog({ ...editDialog, draft: { ...editDialog.draft, suggested: e.target.value } })}
                placeholder="Leave blank for 'correct' verdicts"
                sx={{ mb: 2 }}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Rationale"
                value={editDialog.draft.rationale}
                onChange={(e) => setEditDialog({ ...editDialog, draft: { ...editDialog.draft, rationale: e.target.value } })}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Additional notes (optional, persisted with this override)"
                value={editDialog.draft.notes}
                onChange={(e) => setEditDialog({ ...editDialog, draft: { ...editDialog.draft, notes: e.target.value } })}
                placeholder="e.g. 'discussed with co-author 2026-05-18, confirmed SC-13 is right call'"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEdit}>Cancel</Button>
              {editDialog.finding._edited && (
                <Button
                  color="warning"
                  onClick={() => {
                    resetRow(editDialog.finding.gt_id);
                    closeEdit();
                  }}
                  startIcon={<RestoreIcon />}
                >
                  Revert to Baseline
                </Button>
              )}
              <Button variant="contained" onClick={saveEdit}>Save</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reset confirmation */}
      <Dialog open={resetConfirm} onClose={() => setResetConfirm(false)}>
        <DialogTitle>Reset all overrides?</DialogTitle>
        <DialogContent>
          <Typography>
            This will discard all {editedCount} of your edits and restore the original
            baseline annotations. This cannot be undone unless you've downloaded the JSON.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirm(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={resetAll}>Reset All</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2500}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
}
